import { useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast as sonnerToast } from 'sonner';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useOrganization } from '@/hooks/useOrganization';
import { queryKeys } from '@/lib/queryKeys';
import { STALE_TIME_PRODUCTS } from '@/lib/queryCacheConfig';
import { promotionsService } from '@/services/promotions/promotionsService';
import type { CreatePromotionDefinitionInput } from '@/services/promotions/promotionValidation';
import { FEATURE_FLAGS } from '@/types/featureFlags';

export function usePromotions() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { isEnabled } = useFeatureFlag();
  const { translator } = useContext(LanguageProviderContext);
  const orgId = currentOrganization?.id ?? '';
  const isFeatureEnabled = isEnabled(FEATURE_FLAGS.PROMOTIONS);

  const promotionsQuery = useQuery({
    queryKey: queryKeys.promotions(orgId),
    queryFn: () => promotionsService.listPromotions(orgId),
    enabled: isFeatureEnabled && Boolean(orgId),
    staleTime: STALE_TIME_PRODUCTS.products,
  });

  const promotions = useMemo(
    () => promotionsQuery.data ?? [],
    [promotionsQuery.data]
  );

  const activePromoProductIdSet = useMemo(
    () => new Set(promotions.filter((p) => p.isActive).map((p) => p.promoProductId)),
    [promotions]
  );

  const getActivePromotionForPromoProduct = (productId: string) =>
    promotions.find((p) => p.promoProductId === productId && p.isActive) ?? null;

  const createPromotion = useMutation({
    mutationFn: (input: CreatePromotionDefinitionInput) => promotionsService.createPromotionDefinition(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.products(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.productsForSales(orgId) });
      sonnerToast.success(translator('Promotions.createSuccess'));
    },
    onError: (error: Error) => {
      sonnerToast.error(translator('Promotions.saveError'), { description: error.message });
    },
  });

  const updatePromotion = useMutation({
    mutationFn: ({
      promotionId,
      input,
    }: {
      promotionId: string;
      input: Parameters<typeof promotionsService.updatePromotionDefinition>[2];
    }) => promotionsService.updatePromotionDefinition(orgId, promotionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.products(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.productsForSales(orgId) });
      sonnerToast.success(translator('Promotions.updateSuccess'));
    },
    onError: (error: Error) => {
      sonnerToast.error(translator('Promotions.saveError'), { description: error.message });
    },
  });

  const deletePromotion = useMutation({
    mutationFn: (promotionId: string) => promotionsService.deletePromotionDefinition(orgId, promotionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions(orgId) });
      sonnerToast.success(translator('Promotions.deleteSuccess'));
    },
    onError: (error: Error) => {
      sonnerToast.error(translator('Promotions.deleteError'), { description: error.message });
    },
  });

  return {
    promotions,
    activePromoProductIdSet,
    getActivePromotionForPromoProduct,
    promotionsQuery,
    createPromotion,
    updatePromotion,
    deletePromotion,
  };
}
